(() => {
  const instances = new WeakMap();
  const customGridReady = customElements.whenDefined('collection-grid-new').then(() => {
    if (!customElements.get('chef-collection-grid')) {
      // The collection controller installs its data methods before native listeners start.
      customElements.define('chef-collection-grid', class extends customElements.get('collection-grid-new') {
        connectedCallback() {}
      });
    }
  });
  const node = (tag, className, text) => {
    const item = document.createElement(tag);
    if (className) item.className = className;
    if (text) item.textContent = text;
    return item;
  };
  async function init(root) {
    if (instances.has(root)) return;
    const lifecycle = new AbortController();
    instances.set(root, lifecycle);
    await customGridReady;
    if (!root.isConnected || lifecycle.signal.aborted) return;
    const generalCollection = root.dataset.generalCollection === 'true';
    const customCards = root.dataset.customCards === 'true';
    const browseRefinements = root.dataset.browseRefinements === 'true';
    const grid = root.querySelector('chef-collection-grid, collection-grid-new');
    const productGrid = grid.querySelector('.product-grid');
    const editorialTemplates = root.querySelector('[data-cc-editorial-templates]');
    const editorialMobile = editorialTemplates ? matchMedia('(max-width:989px)') : null;
    const strings = JSON.parse(root.querySelector('[data-cc-strings]').textContent);
    const media = window.chefCollectionMedia || {};
    const liveCards = root.dataset.liveCards === 'true';
    const hybridCards = root.dataset.hybridCards === 'true';
    const surfaceCards = root.dataset.surfaceCards === 'true';
    const photoHoverCards = root.dataset.photoHover === 'true';
    const labCards = root.dataset.labCards === 'true';
    const materialHover = root.dataset.materialHover === 'true';
    const revisionMedia = materialHover ? window.chefCollectionV8Media || {} : {};
    const labMedia = labCards ? window.chefCollectionLabMedia || {} : {};
    const alternateMedia = window.chefCollectionAlternateMedia || {};
    const details = JSON.parse(root.querySelector('[data-cc-product-details]')?.textContent || '{}');
    const finderStrings = JSON.parse(root.querySelector('[data-cc-finder-strings]').textContent);
    const finderVisuals = root.querySelector('[data-cc-finder-visuals]');
    const status = root.querySelector('[data-cc-status]');
    const dialogs = [...root.querySelectorAll('[data-cc-dialog]')];
    let opener, chosenSize = 'everyday', pending = null;
    const on = (target, event, handler) => target.addEventListener(event, handler, { signal: lifecycle.signal });
    if (customCards) {
      // Reuse the theme's native cart transport for initial and appended Liquid forms.
      // Card images and hover states remain entirely Liquid/CSS.
      on(root, 'submit', event => {
        const form = event.target.closest('[data-cc-product-form]');
        if (!form || !window.theme?.AjaxProduct) return;
        const handler = new theme.AjaxProduct(form);
        handler.addToCart = form.querySelector('button[type="submit"]');
        handler.addItemFromForm(event);
      });
      on(root, 'ajaxProduct:added', event => {
        if (event.target.matches('[data-cc-product-form]')) document.dispatchEvent(new CustomEvent('cart:build'));
      });
    }
    const dialogFor = name => dialogs.find(dialog => dialog.dataset.ccDialog === name);
    const open = (name, trigger) => {
      const dialog = dialogFor(name);
      if (!dialog) return;
      opener = trigger;
      dialog.showModal();
      dialog.querySelector('[data-cc-close]').focus();
      if (materialHover) dialog.scrollTop = 0;
      if (name === 'finder') startFinder();
      if (name === 'video') {
        const video = dialog.querySelector('video');
        if (!video.src) video.src = video.dataset.src;
        video.play().catch(() => {});
      }
    };
    dialogs.forEach(dialog => {
      on(dialog, 'close', () => {
        dialog.querySelectorAll('video').forEach(video => video.pause());
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      });
      on(dialog, 'click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      });
    });
    on(root, 'click', event => {
      const trigger = event.target.closest('[data-cc-open]');
      if (trigger) open(trigger.dataset.ccOpen, trigger);
      const close = event.target.closest('[data-cc-close]');
      if (close) close.closest('dialog').close();
      const quick = event.target.closest('[data-cc-quick-look]');
      if (quick) showQuick(quick.closest('.new-card'), quick);
      const change = event.target.closest('[data-cc-switch]');
      if (change) { change.closest('dialog').close(); open(change.dataset.ccSwitch, root.querySelector('.cc-finder')); }
    });
    const backToTop = root.querySelector('[data-cc-back-to-top]');
    if (backToTop) {
      let scrollFrame = 0;
      const updateBackToTop = () => {
        scrollFrame = 0;
        const box = root.getBoundingClientRect();
        backToTop.hidden = box.top > -Math.max(600, window.innerHeight) || box.bottom <= 0;
      };
      const scheduleBackToTop = () => {
        if (!scrollFrame) scrollFrame = requestAnimationFrame(updateBackToTop);
      };
      window.addEventListener('scroll', scheduleBackToTop, {passive:true, signal:lifecycle.signal});
      on(window, 'resize', scheduleBackToTop);
      on(backToTop, 'click', () => {
        const heading = root.querySelector('h1');
        const top = Math.max(0, heading.getBoundingClientRect().top + window.scrollY - 100);
        heading.focus({preventScroll:true});
        window.scrollTo({top, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      });
      lifecycle.signal.addEventListener('abort', () => cancelAnimationFrame(scrollFrame));
      updateBackToTop();
    }
    const originalPrepare = grid.prepareCustomProductCardsData;
    grid.prepareCustomProductCardsData = function () { this.customCards = []; this.customCardsData = []; };
    const originalFetch = grid.fetchCollectionProducts.bind(grid);
    if (!customCards) grid.fetchCollectionProducts = async function (...args) {
      status.textContent = strings.ui.loading;
      root.querySelectorAll('[data-cc-filter], [data-cc-sort]').forEach(button => button.disabled = true);
      try {
        pending = originalFetch(...args);
        await pending;
        status.textContent = this.productsFetched ? '' : strings.ui.error;
        return this.productsFetched;
      } finally {
        pending = null;
        root.querySelectorAll('[data-cc-filter], [data-cc-sort]').forEach(button => button.disabled = false);
        sync();
      }
    };
    async function loaded() {
      if (customCards) return grid.fetchCollectionProducts(false);
      if (pending) await pending;
      if (!grid.productsFetched) await grid.fetchCollectionProducts(false);
      return grid.productsFetched;
    }
    const pickers = grid.querySelector('.pickers-wrapper');
    const sortLabel = node('label', 'cc-sort');
    sortLabel.append(node('span', '', strings.ui.sort));
    const sort = node('select'); sort.dataset.ccSort = ''; sort.setAttribute('aria-label', strings.ui.sort);
    [['COLLECTION_DEFAULT:false','featured'],['BEST_SELLING:false','popular'],['CREATED:true','newest'],['PRICE:false','price_low'],['PRICE:true','price_high']].forEach(([value,key]) => { const option = node('option', '', strings.ui[key]); option.value = value; [option.dataset.sortKey, option.dataset.reverse] = value.split(':'); sort.append(option); });
    sortLabel.append(sort); pickers.append(sortLabel);
    on(sort, 'change', () => {
      const [key, reverse] = sort.value.split(':');
      const item = browseRefinements ? sort.selectedOptions[0] : [...grid.querySelectorAll('.sort-picker-item')].find(item => item.dataset.sortKey === key && item.dataset.reverse === reverse);
      if (item) grid.updateSort(item);
    });
    on(root.querySelector('.cc-quick'), 'click', async event => {
      const button = event.target.closest('[data-cc-filter]');
      if (!button || !(await loaded())) return;
      if (button.dataset.ccFilter === 'all') { grid.selectedOptions = []; grid.currentMinPrice = null; grid.currentMaxPrice = null; }
      else if (grid.selectedOptions.includes(button.dataset.ccFilter)) grid.selectedOptions = grid.selectedOptions.filter(value => value !== button.dataset.ccFilter);
      else grid.selectedOptions.push(button.dataset.ccFilter);
      grid.filterAndRenderProducts(); sync();
    });
    function unfiltered() {
      return !grid.selectedOptions.length && (grid.currentMinPrice === null || grid.currentMinPrice <= grid.allMinPrice) && (grid.currentMaxPrice === null || grid.currentMaxPrice >= grid.allMaxPrice);
    }
    function sync() {
      root.querySelectorAll('[data-cc-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ccFilter === 'all' ? unfiltered() : grid.selectedOptions.includes(button.dataset.ccFilter))));
      sort.value = grid.currentSortKey + ':' + String(grid.currentReverse);
      const selection = root.querySelector('[data-cc-selection]');
      const labels = grid.selectedOptions.filter(value => value !== 'In Stock');
      if (grid.selectedOptions.includes('In Stock')) labels.unshift(finderStrings.available);
      if (grid.currentMinPrice !== null && grid.currentMinPrice > grid.allMinPrice) labels.push(finderStrings.from + ' ' + finderMoney(grid.currentMinPrice));
      if (grid.currentMaxPrice !== null && grid.currentMaxPrice < grid.allMaxPrice) labels.push(finderStrings.up_to + ' ' + finderMoney(grid.currentMaxPrice));
      selection.hidden = !labels.length;
      selection.querySelector('[data-cc-selection-copy]').textContent = labels.join(' · ');
      if (surfaceCards) syncChips();
    }
    function syncChips() {
      const container = root.querySelector('[data-cc-active-chips]');
      const chips = grid.selectedOptions.filter(value => value !== 'Best Sellers' || !root.querySelector('[data-cc-filter="Best Sellers"]')).map(value => ({key:value,label:value === 'In Stock' ? finderStrings.available : value}));
      const prices = [];
      if (grid.currentMinPrice !== null && grid.currentMinPrice > grid.allMinPrice) prices.push(finderStrings.from + ' ' + finderMoney(grid.currentMinPrice));
      if (grid.currentMaxPrice !== null && grid.currentMaxPrice < grid.allMaxPrice) prices.push(finderStrings.up_to + ' ' + finderMoney(grid.currentMaxPrice));
      if (prices.length) chips.push({key:'price',label:prices.join(' · ')});
      const signature = JSON.stringify(chips);
      if (container.dataset.signature !== signature) {
        container.dataset.signature = signature;
        container.replaceChildren(...chips.map(({key,label}) => {
          const button = node('button','cc-filter-chip',label), cross = node('span','','×');
          button.type = 'button'; button.dataset.ccRemoveFilter = key;
          button.setAttribute('aria-label',strings.ui.remove_filter.replace('{filter}',label));
          cross.setAttribute('aria-hidden','true'); button.append(cross); return button;
        }));
      }
      const best = root.querySelector('[data-cc-filter="Best Sellers"]');
      if (best) {
        const selected = grid.selectedOptions.includes('Best Sellers');
        best.querySelector('.cc-chip-cross')?.remove();
        if (selected) { const cross = node('span','cc-chip-cross','×'); cross.setAttribute('aria-hidden','true'); best.append(cross); }
        best.setAttribute('aria-label',selected ? strings.ui.remove_filter.replace('{filter}',best.firstChild.textContent.trim()) : best.textContent.trim());
      }
      root.querySelector('[data-cc-clear-chips]').hidden = unfiltered();
    }
    if (surfaceCards) {
      on(root.querySelector('[data-cc-active-chips]'),'click',event => {
        const chip = event.target.closest('[data-cc-remove-filter]'); if (!chip) return;
        const index = [...chip.parentElement.children].indexOf(chip);
        if (chip.dataset.ccRemoveFilter === 'price') {grid.currentMinPrice=null;grid.currentMaxPrice=null;}
        else grid.selectedOptions=grid.selectedOptions.filter(value=>value!==chip.dataset.ccRemoveFilter);
        grid.filterAndRenderProducts(); sync();
        const remaining=root.querySelector('[data-cc-active-chips]').children;
        const allFilter = root.querySelector('[data-cc-filter="all"]');
        const fallback = allFilter.getClientRects().length ? allFilter : grid.querySelector('.option-picker-btn');
        (remaining[Math.min(index,remaining.length-1)] || fallback).focus({preventScroll:true});
      });
      on(root.querySelector('[data-cc-clear-chips]'),'click',()=>root.querySelector('[data-cc-filter="all"]').click());
    }
    let finderStep = 0, finderAnswer = {size:'any',budget:'any',style:'any'};
    const finderMoney = value => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:root.dataset.currency,minimumFractionDigits:0,maximumFractionDigits:2}).format(value);
    const finder = dialogFor('finder');
    function finderCriteria() {
      const lengths = Object.entries(grid.currentFilterTypes).find(([key]) => /(?:blade|knife).*length/i.test(key))?.[1] || [];
      const series = Object.entries(grid.currentFilterTypes).find(([key]) => /^series$/i.test(key))?.[1] || [];
      const selectedLengths = finderAnswer.size === 'any' ? [] : lengths.filter(value => {
        const size = parseFloat(value);
        return finderAnswer.size === 'compact' ? size >= 6 && size < 8 : finderAnswer.size === 'everyday' ? size >= 8 && size < 9 : size >= 9;
      });
      const styleSeries = {classic:/gladiator|phantom|vanquish/i,damascus:/shogun/i,bold:/valhalla|shadow black|scorpion|spartan ghost|night shark|barbarian|firestorm/i};
      const selectedSeries = finderAnswer.style === 'any' ? [] : series.filter(value => styleSeries[finderAnswer.style].test(value));
      const bands = {any:[null,null],under:[null,99.99],middle:[100,199.99],above:[200,null]};
      const [min,max] = bands[finderAnswer.budget];
      const unsupported = (finderAnswer.size !== 'any' && !selectedLengths.length) || (finderAnswer.style !== 'any' && !selectedSeries.length);
      return {selectedLengths,selectedSeries,min,max,unsupported};
    }
    function finderCount(criteria) {
      if (!grid.productsFetched || criteria.unsupported) return 0;
      return (grid.allProducts || []).filter(({node:product}) => {
        const price = grid.getFinalPrice(product);
        return product.availableForSale && (!criteria.selectedLengths.length || criteria.selectedLengths.some(value => product.tags.includes(value))) &&
          (!criteria.selectedSeries.length || criteria.selectedSeries.some(value => product.tags.includes(value))) &&
          (criteria.min === null || price >= criteria.min) && (criteria.max === null || price <= criteria.max);
      }).length;
    }
    function updateFinder() {
      const next = finder.querySelector('[data-cc-finder-next]');
      const message = finder.querySelector('[data-cc-finder-status]');
      if (finderStep < 2) { next.disabled = false; next.textContent = finderStrings.next + ' →'; message.textContent = ''; return; }
      const count = finderCount(finderCriteria());
      next.disabled = !grid.productsFetched || !count;
      next.textContent = grid.productsFetched ? finderStrings.matches.replace('{count}',count) : strings.ui.loading;
      message.textContent = grid.productsFetched && !count ? finderStrings.no_matches : '';
    }
    function renderFinder(focus = false) {
      const key = ['size','budget','style'][finderStep];
      const heading = finder.querySelector('h2'); heading.textContent = finderStrings['choose_'+key];
      finder.querySelector('[data-cc-finder-description]').textContent = finderStrings[key+'_intro'];
      finder.querySelector('[data-cc-finder-step]').textContent = finderStrings.step.replace('{step}',finderStep+1);
      finder.querySelectorAll('.cc-finder-progress span').forEach((item,index) => item.classList.toggle('is-current',index<=finderStep));
      const fieldset = node('fieldset');
      const legend = node('legend','cc-sr-only',finderStrings['choose_'+key]); fieldset.append(legend);
      let options;
      if (key === 'size') options = ['compact','everyday','long','any'].map(value => [value,finderStrings[value+'_size'],finderStrings[value+'_size_copy']]);
      if (key === 'budget') options = [['under',finderStrings.under+' '+finderMoney(100)],['middle',finderMoney(100)+'–'+finderMoney(199.99)],['above',finderMoney(200)+'+'],['any',finderStrings.any_budget]];
      if (key === 'budget' && browseRefinements) options = options.filter(([value]) => value !== 'under');
      if (key === 'style') options = ['classic','damascus','bold','any'].map(value => [value,finderStrings[value+'_style'],finderStrings[value+'_style_copy']]);
      options.forEach(([value,title,description]) => {
        const label = node('label','cc-finder-option'), input = document.createElement('input'), copy = node('span');
        input.type = 'radio'; input.name = 'cc-finder-'+key+'-'+root.dataset.sectionId; input.value = value; input.checked = finderAnswer[key] === value;
        copy.append(node('strong','',title)); if (description) copy.append(node('small','',description));
        label.append(input);
        const visual = finderVisuals?.content.querySelector('[data-cc-choice="'+key+'-'+value+'"]');
        if (visual) label.append(visual.content.cloneNode(true));
        label.append(copy); fieldset.append(label);
        input.addEventListener('change',() => {finderAnswer[key] = value;updateFinder();});
      });
      finder.querySelector('[data-cc-finder-options]').replaceChildren(fieldset);
      finder.querySelector('[data-cc-finder-back]').hidden = finderStep === 0;
      updateFinder();
      if (finderVisuals) finder.scrollTop = 0;
      if (focus) heading.focus({ preventScroll: !!finderVisuals });
    }
    async function startFinder() {
      finderStep = 0; renderFinder();
      if (await loaded()) updateFinder(); else finder.querySelector('[data-cc-finder-status]').textContent = strings.ui.error;
    }
    on(finder.querySelector('[data-cc-finder-back]'),'click',() => {finderStep=Math.max(0,finderStep-1);renderFinder(true);});
    on(finder.querySelector('[data-cc-finder-next]'),'click',() => {
      if (finderStep < 2) {finderStep++;renderFinder(true);return;}
      const criteria = finderCriteria(); if (!finderCount(criteria)) return;
      grid.selectedOptions = ['In Stock',...criteria.selectedLengths,...criteria.selectedSeries];
      grid.currentMinPrice = criteria.min; grid.currentMaxPrice = criteria.max;
      grid.filterAndRenderProducts(); finder.close(); sync(); grid.scrollIntoView({block:'start',behavior:'auto'});
    });
    on(root.querySelector('[data-cc-clear]'),'click',() => root.querySelector('[data-cc-filter="all"]').click());
    function handleOf(card) {
      if (card.dataset.productHandle) return card.dataset.productHandle;
      const url = card.querySelector('.grid-product__link')?.href;
      if (!url) return '';
      return new URL(url, location.href).pathname.split('/products/')[1] || '';
    }
    const clean = value => String(value || '').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;|&apos;/g, "'").replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().replace(/\.$/, '');
    function setContents(fact) {
      if (!/\b(?:piece|pc)\b/i.test(fact.title || '')) return '';
      const knives = (fact.included || []).filter(item => /knife/i.test(item) && !/sheath/i.test(item));
      if (knives.length > 1) return knives.map(item => {
        const size = item.match(/(\d+(?:\.\d+)?)\s*["″”]/)?.[1];
        const kind = item.match(/chef|paring|santoku|utility|bread|cleaver|slicer/i)?.[0];
        return size && kind ? size + '″ ' + kind.toLowerCase() : clean(item);
      }).join(' + ');
      // Some licensed sets expose their components in the product title instead.
      const titled = fact.title.match(/\d+(?:\.\d+)?\s*["″”]\s*(?:Chef['’]?s?|Paring|Santoku|Utility)\s*Knife/gi);
      return titled?.length > 1 ? titled.map(item => item.replace(/\s*Knife/i, '').replace(/Chef['’]?s?/i, 'chef').replace(/Paring/i, 'paring')).join(' + ') : '';
    }
    function productMaterials(card, fact) {
      const title = fact.title || card.querySelector('.collection-product__product-title')?.textContent || '';
      let blade = clean(fact.blade), handle = clean(fact.handle);
      if (/AUS-10V/i.test(blade)) blade = 'Japanese AUS-10V steel' + (/Damascus/i.test(blade) ? ' · Damascus cladding' : '');
      if (/Unique one of a kind/i.test(handle)) handle = handle.replace(/Unique one of a kind\s*/i, '');
      if (/olive wood/i.test(title)) handle = 'Olive wood';
      else if (/G10.* OR .*G10/i.test(handle)) handle = 'G10 fiberglass composite';
      else if (/\sOR\s/.test(handle)) handle = '';
      const nativeSpec = clean(card.querySelector('.collection-product__collection-specs')?.textContent);
      if (!blade && nativeSpec) blade = nativeSpec.split('|').find(part => /steel|AUS|CR\d|S35VN|V12|14C28N|X50/i.test(part))?.trim() || '';
      if (!handle && nativeSpec) handle = nativeSpec.split('|').find(part => /G10|ABS|resin|wood|POM|polymer|micarta/i.test(part))?.trim() || '';
      return {blade,handle};
    }
    function cardMaterials(card, fact) {
      let {blade,handle} = productMaterials(card,fact);
      const grade = blade.match(/(?:AUS[- ]?10V|9CR18MOV|7CR17MOV|5CR15MOV|S35VN|V12\s*Ultra[™]?|14C28N|X50CrMoV15|10Cr15CoMoV)/i)?.[0];
      if (grade) blade = grade;
      else if (/German/i.test(blade)) blade = 'German steel';
      else if (/Japanese/i.test(blade)) blade = 'Japanese steel';
      if (/resin/i.test(handle) && /wood/i.test(handle)) handle = 'Resin & wood';
      else if (/G10/i.test(handle)) handle = 'G10';
      else if (/ABS/i.test(handle)) handle = 'ABS';
      else if (/POM/i.test(handle)) handle = 'POM';
      else if (/resin/i.test(handle)) handle = 'Resin';
      else if (/olive wood/i.test(handle)) handle = 'Olive wood';
      else if (/rosewood/i.test(handle)) handle = 'Rosewood';
      else if (/wenge/i.test(handle)) handle = 'Wenge wood';
      return [blade,handle].filter(Boolean).join(' · ') || strings.ui.materials_details;
    }
    function quickFacts(card, fact) {
      const rows = [], title = fact.title || card.querySelector('.collection-product__product-title')?.textContent || '';
      const useKey = /piece|pc\b/i.test(title) ? 'use_set' : /serrated/i.test(title) ? 'use_serrated' : /cleaver|serbian/i.test(title) ? 'use_cleaver' : /kiritsuke/i.test(title) ? 'use_kiritsuke' : 'use_chef';
      if (!generalCollection) rows.push([strings.ui.use, strings.ui[useKey]]);
      const {blade,handle} = productMaterials(card,fact);
      if (blade) rows.push([strings.ui.steel, blade]);
      if (handle) rows.push([strings.ui.handle, handle]);
      const sheaths = (fact.included || []).filter(item => /sheath/i.test(item));
      if (sheaths.length) {
        const specific = sheaths.find(item => /leatherette|nylon|thermoplastic/i.test(item));
        rows.push([strings.ui.included, specific || (sheaths.length > 1 ? strings.ui.protective_sheaths : strings.ui.protective_sheath)]);
      } else if (fact.sheath) rows.push([strings.ui.sheath, clean(fact.sheath)]);
      return rows;
    }
    function showQuick(card, trigger) {
      const dialog = dialogFor('quick'), layout = node('div', 'cc-quick-layout');
      const fact = details[handleOf(card)] || {};
      const image = card.querySelector('.grid-product__image')?.cloneNode();
      if (image) {
        image.className = 'cc-quick-image'; image.removeAttribute('style'); image.loading = 'eager'; image.removeAttribute('id'); image.removeAttribute('aria-hidden');
        layout.append(surfaceCards ? photoGallery(image,fact) : image);
      }
      const copy = node('div', 'cc-quick-copy');
      copy.append(node('p', 'cc-eyebrow', card.querySelector('.collection-product__series-title')?.textContent.trim()));
      const title = node('h2', '', card.querySelector('.collection-product__product-title')?.textContent.trim()); title.id = 'cc-quick-title-' + root.dataset.sectionId;
      dialog.setAttribute('aria-labelledby', title.id); copy.append(title);
      const contents = setContents(fact);
      if (contents) copy.append(node('p', 'cc-quick-spec', contents));
      const facts = node('ul', 'cc-facts');
      quickFacts(card, fact).forEach(([label, text]) => {
        const item = node('li'); item.append(node('strong', '', label + ': '), document.createTextNode(text)); facts.append(item);
      });
      copy.append(facts);
      const price = card.querySelector('.grid-collection-product__price')?.cloneNode(true);
      if (price) { price.className = 'cc-quick-price'; price.querySelectorAll('[id]').forEach(item => item.removeAttribute('id')); price.querySelector('.cc-material-toggle')?.remove(); copy.append(price); }
      const sourceLink = card.querySelector('.grid-product__link');
      const actions = node('div', 'cc-quick-actions');
      const sourceButton = card.querySelector('button.add-to-cart:not(.notify-me-btn):not([data-external-checkout-trigger])');
      if (sourceButton && !sourceButton.disabled && (fact.variants || 1) === 1) {
        const add = node('button', 'cc-primary', strings.ui.add_to_cart); add.type = 'button';
        // Delegate once to this card's existing native cart handler; never add a second request handler.
        add.addEventListener('click', () => { add.disabled = true; dialog.close(); sourceButton.click(); });
        actions.append(add);
      }
      if (sourceLink?.hasAttribute('data-external-checkout-trigger')) {
        const link = node('button', 'cc-primary', strings.ui.external + ' ↗'); link.type = 'button';
        link.addEventListener('click', () => { dialog.close(); sourceLink.click(); }); actions.append(link);
      } else if (sourceLink) { const link = node('a', 'cc-detail-link', ((fact.variants || 1) > 1 ? strings.ui.choose_options : strings.ui.view_details) + ' →'); link.href = sourceLink.href; actions.append(link); }
      copy.append(actions);
      layout.append(copy); root.querySelector('[data-cc-quick-content]').replaceChildren(layout); open('quick', trigger);
    }
    function photoGallery(first, fact) {
      const gallery=node('div','cc-gallery'), track=node('div','cc-gallery-track'), controls=node('div','cc-gallery-controls');
      gallery.setAttribute('role','region'); gallery.setAttribute('aria-label',strings.ui.gallery);
      track.tabIndex=0; track.setAttribute('aria-label',strings.ui.gallery);
      track.id='cc-gallery-'+root.dataset.sectionId;
      const photos=[first,...(fact.gallery || []).slice(1).map(photo=>{
        const image=node('img','cc-quick-image'); image.src=photo.src; image.alt=photo.alt || fact.title;
        image.srcset=[320,640,1000].map(width=>{const url=new URL(photo.src,location.href);url.searchParams.set('width',width);return url.href+' '+width+'w';}).join(',');
        image.loading='lazy';image.decoding='async';image.width=1000;image.height=1000;return image;
      })];
      photos.forEach(image=>{image.sizes='(min-width: 701px) 460px, 88vw';const slide=node('div','cc-gallery-slide');slide.append(image);track.append(slide);});
      gallery.append(track);
      if(photos.length<2)return gallery;
      const previous=node('button','cc-gallery-arrow','←'),next=node('button','cc-gallery-arrow','→'),count=node('span','cc-gallery-count'),dots=node('div','cc-gallery-dots');
      previous.type=next.type='button';previous.setAttribute('aria-label',strings.ui.previous_photo);next.setAttribute('aria-label',strings.ui.next_photo);
      [previous,next].forEach(button=>button.setAttribute('aria-controls',track.id));
      count.setAttribute('aria-live','polite');count.setAttribute('aria-atomic','true');
      let current=0;
      const go=index=>track.scrollTo({left:Math.max(0,Math.min(photos.length-1,index))*track.clientWidth,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      photos.forEach((photo,index)=>{const dot=node('button','cc-gallery-dot');dot.type='button';dot.setAttribute('aria-label',strings.ui.photo.replace('{number}',index+1));dot.setAttribute('aria-controls',track.id);dot.addEventListener('click',()=>go(index));dots.append(dot);});
      function update(){current=Math.round(track.scrollLeft/track.clientWidth)||0;previous.disabled=current===0;next.disabled=current===photos.length-1;count.textContent=strings.ui.photo_count.replace('{number}',current+1).replace('{total}',photos.length);[...dots.children].forEach((dot,index)=>dot.setAttribute('aria-pressed',String(index===current)));}
      previous.addEventListener('click',()=>go(current-1));next.addEventListener('click',()=>go(current+1));
      track.addEventListener('scroll',update,{passive:true});
      track.addEventListener('keydown',event=>{if(event.key==='ArrowRight'||event.key==='ArrowLeft'){event.preventDefault();go(current+(event.key==='ArrowRight'?1:-1));}});
      controls.append(previous,dots,count,next);gallery.append(controls);update();return gallery;
    }
    function revealMaterials(card,stage,fact) {
      const panel=node('div','cc-material-panel'),button=node('button','cc-material-toggle',strings.ui.details+' +');
      panel.id='cc-material-'+root.dataset.sectionId+'-'+handleOf(card); panel.hidden=true;
      const materials=productMaterials(card,fact);
      [[strings.ui.steel,materials.blade],[strings.ui.handle,materials.handle]].filter(([,value])=>value).forEach(([label,value])=>{const row=node('p');row.append(node('strong','',label+': '),document.createTextNode(value));panel.append(row);});
      if(!panel.children.length)panel.append(node('p','',cardMaterials(card,fact)));
      button.type='button';button.setAttribute('aria-expanded','false');button.setAttribute('aria-controls',panel.id);
      let pinned=false,hovered=false;
      const update=()=>{const visible=pinned||hovered;panel.hidden=!visible;button.setAttribute('aria-expanded',String(visible));button.textContent=strings.ui.details+(visible?' −':' +');};
      button.addEventListener('click',()=>{pinned=!pinned;hovered=false;update();});
      if (!photoHoverCards) card.addEventListener('pointerenter',event=>{if(event.pointerType==='mouse'&&matchMedia('(hover:hover) and (pointer:fine)').matches){hovered=true;update();}});
      card.addEventListener('pointerleave',()=>{hovered=false;update();});
      card.addEventListener('keydown',event=>{if(event.key==='Escape'){pinned=false;hovered=false;update();}});
      if (photoHoverCards) card.querySelector('.collection-product__info-wrapper')?.append(panel);
      else stage.append(panel);
      const price=card.querySelector('.grid-collection-product__price');
      if(price){const values=node('span','cc-price-values');while(price.firstChild)values.append(price.firstChild);price.append(values,button);}
      const contents=setContents(fact);
      if(contents)card.querySelector('.collection-product__title')?.after(node('div','cc-set-contents',contents));
    }
    function alternatePhoto(card, stage, toolbar) {
      const lab = labMedia[handleOf(card)];
      const curated = revisionMedia[handleOf(card)];
      const photo = materialHover ? curated?.photo : lab ? {src:lab.hover,alt:lab.label} : alternateMedia[handleOf(card)];
      const primary = stage.querySelector('.grid-product__image'), link = primary?.closest('a');
      if (!photo || !primary || !link) return;
      const image = node('img', 'cc-alternate-image');
      image.id = 'cc-alternate-' + root.dataset.sectionId + '-' + handleOf(card);
      image.alt = photo.alt; image.width = 1000; image.height = 1000; image.decoding = 'async';
      image.style.objectPosition = photo.position || '50% 50%';
      if (lab && !materialHover) image.style.transformOrigin = lab.fx + '% ' + lab.fy + '%';
      if (materialHover) {
        image.style.transformOrigin = photo.position || '50% 50%';
        image.style.setProperty('--cc-detail-scale', photo.scale || 1.14);
        card.dataset.ccPhotoKind = photo.kind;
      }
      image.setAttribute('aria-hidden', 'true');
      const button = node('button', 'cc-photo-toggle'); button.type = 'button';
      button.setAttribute('aria-controls', image.id);
      const name = card.querySelector('.collection-product__product-title')?.textContent.trim() || '';
      let loaded = false, requested = false, failed = false, hovered = false, pinned = false;
      let caption;
      const magnifier = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true" focusable="false"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 4.5 4.5"/></svg>';
      if (materialHover) {
        const spec = card.querySelector('.collection-product__collection-specs');
        const text = spec?.textContent.trim() || cardMaterials(card, details[handleOf(card)] || {});
        caption = node('div', 'cc-material-caption');
        const icon = node('span', 'cc-material-icon'); icon.setAttribute('aria-hidden', 'true'); icon.innerHTML = magnifier;
        caption.append(icon, node('span', '', text));
        caption.setAttribute('aria-hidden', 'true'); stage.append(caption);
      }
      function update() {
        const visible = loaded && (hovered || pinned);
        if (caption) caption.setAttribute('aria-hidden', String(!visible));
        card.dataset.ccAlternate = String(visible);
        image.setAttribute('aria-hidden', String(!visible));
        primary.setAttribute('aria-hidden', String(visible));
        button.setAttribute('aria-pressed', String(visible));
        button.setAttribute('aria-label', (visible ? strings.ui.primary_photo : strings.ui.alternate_photo) + ': ' + name);
        if (labCards) {
          if (materialHover && !visible) button.innerHTML = magnifier;
          else button.textContent = visible ? '×' : '⌕';
          button.title = button.getAttribute('aria-label');
        } else button.textContent = strings.ui.card_photo.replace('{number}', visible ? '2' : '1') + ' ↔';
      }
      function load() {
        if (requested || failed) return;
        requested = true;
        const sized = width => { const url = new URL(photo.src, location.href); url.searchParams.set('width', width); url.searchParams.set('format', 'webp'); return url.href; };
        image.sizes = primary.sizes; image.srcset = [350, 500, 700, 1000].map(width => sized(width) + ' ' + width + 'w').join(',');
        image.src = sized(700); link.append(image);
      }
      image.addEventListener('load', () => { loaded = true; update(); });
      image.addEventListener('error', () => { failed = true; loaded = false; hovered = pinned = false; update(); button.hidden = true; image.remove(); caption?.remove(); });
      card.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' && matchMedia(labCards ? '(min-width:990px) and (hover:hover) and (pointer:fine)' : '(min-width:701px) and (hover:hover) and (pointer:fine)').matches) { hovered = true; load(); update(); }
      });
      card.addEventListener('pointerleave', () => { hovered = false; update(); });
      button.addEventListener('focus', load);
      button.addEventListener('click', () => { pinned = !(pinned || hovered); hovered = false; load(); update(); });
      card.addEventListener('keydown', event => { if (event.key === 'Escape') { pinned = hovered = false; update(); } });
      if (labCards) {
        stage.append(button);
        if (lab && !materialHover) {
          const caption = node('div','cc-lab-caption',lab.label);
          card.querySelector('.collection-product__title')?.after(caption);
        }
      } else toolbar.insertBefore(button, toolbar.querySelector('.cc-quick-button'));
      update();
    }
    function syncEditorialCards(cards) {
      const existing = new Map([...productGrid.querySelectorAll('[data-cc-editorial-id]')].map(tile => [tile.dataset.ccEditorialId, tile]));
      if (!unfiltered() || grid.currentSortKey !== 'COLLECTION_DEFAULT') {
        existing.forEach(tile => tile.remove());
        return;
      }
      const anchors = new Map();
      editorialTemplates.querySelectorAll('template[data-cc-editorial-template]').forEach(template => {
        const id = template.dataset.ccEditorialTemplate;
        const position = Number(editorialMobile.matches ? template.dataset.mobileAfter : template.dataset.desktopAfter);
        const anchor = anchors.get(position) || cards[position - 1];
        let tile = existing.get(id);
        if (!anchor) { tile?.remove(); return; }
        if (!tile) tile = template.content.firstElementChild.cloneNode(true);
        if (tile.previousElementSibling !== anchor) anchor.after(tile);
        anchors.set(position, tile);
      });
    }
    function enhance() {
      if (!liveCards || hybridCards) productGrid.querySelectorAll('.new-card').forEach(card => {
        if (card.classList.contains('card-product-custom')) return;
        if (card.dataset.ccEnhanced) return;
        card.dataset.ccEnhanced = 'true';
        const lab = labMedia[handleOf(card)];
        const curated = revisionMedia[handleOf(card)];
        const source = curated?.hero || lab?.hero || media[handleOf(card)], image = card.querySelector('.grid-product__image');
        if (source && image) {
          image.src = source + '&width=700'; image.srcset = [350,500,700,1000].map(width => source + '&width=' + width + ' ' + width + 'w').join(',');
          image.sizes = surfaceCards ? '(min-width: 1384px) 427px, (min-width: 701px) 32vw, 48vw' : '(min-width: 1100px) 24vw, (min-width: 701px) 32vw, 48vw'; image.width = 1000; image.height = 1000;
          image.removeAttribute('data-src'); image.removeAttribute('data-srcset');
          if (materialHover && curated) {
            card.dataset.ccPhotoFormat = curated.format;
          }
          if (lab && (!materialHover || curated?.format === 'lab')) {
            card.dataset.ccLabImage = 'true';
            image.height = 1250;
            image.sizes = '(min-width: 1286px) 394px, (min-width: 990px) calc((100vw - 104px) / 3), calc((100vw - 36px) / 2)';
            const picture = node('picture'), phone = node('source');
            phone.media = '(max-width:989px)'; phone.sizes = 'calc((100vw - 36px) / 2)';
            phone.srcset = [350,600,1000].map(width => (curated?.phone || lab.phone) + '&width=' + width + ' ' + width + 'w').join(',');
            image.before(picture); picture.append(phone,image);
          }
        }
        const stage = card.querySelector('.grid-product__image-mask');
        if (!hybridCards) {
          const fact = details[handleOf(card)] || {};
          const title = card.querySelector('.collection-product__product-title');
          const price = card.querySelector('.grid-collection-product__price');
          const titleGroup = card.querySelector('.collection-product__title');
          if (title && price && titleGroup) {
            const heading = node('div', 'cc-card-heading'); heading.append(title, price); titleGroup.append(heading);
          }
          const contents = setContents(fact);
          let subline = card.querySelector('.collection-product__collection-specs-short');
          if (!subline) { subline = node('div', 'collection-product__collection-specs-short'); titleGroup?.after(subline); }
          subline.textContent = cardMaterials(card,fact);
          if (contents) subline.before(node('div','cc-set-contents',contents));
          card.classList.add('cc-card-ready');
        }
        if (stage) {
          if (materialHover && curated?.logo) {
            const logo = node('img', 'cc-license-mark');
            logo.src = curated.logo.src; logo.alt = curated.logo.alt;
            logo.width = 240; logo.height = 138; logo.loading = 'lazy';
            stage.append(logo);
          }
          const button = node('button', 'cc-quick-button', strings.ui.quick_look + ' +'); button.type = 'button';
          button.setAttribute('aria-label', strings.ui.quick_look + ': ' + card.querySelector('.collection-product__product-title')?.textContent.trim());
          button.addEventListener('click', () => showQuick(card, button));
          const toolbar = node('div','cc-card-toolbar');
          if (hybridCards) {
            // Keep the recognition mark readable without shrinking it into the toolbar.
            card.querySelectorAll('.badge--netflix').forEach(badge => stage.append(badge));
            // The native renderer reserves promotion space even when no message exists.
            card.querySelectorAll('.collection-product__urgency-line').forEach(line => {
              if (!line.textContent.trim() && !line.children.length) line.hidden = true;
            });
            const badges = node('div','cc-card-badges');
            card.querySelectorAll('.badge-overlay, .badge--daily-deal').forEach(badge => badges.append(badge));
            card.querySelectorAll('.grid-collection-product__price .grid-product__tag--best-value, .grid-collection-product__price .grid-product__tag--best-seller').forEach(badge => badges.append(badge));
            toolbar.append(badges);
          } else {
            const badges = card.querySelector('.badge-overlay');
            if (badges) toolbar.append(badges);
          }
          toolbar.append(button); stage.after(toolbar);
          if (surfaceCards && !labCards) revealMaterials(card,stage,details[handleOf(card)] || {});
          if (labCards) {
            const contents = setContents(details[handleOf(card)] || {});
            if (contents) card.querySelector('.collection-product__title')?.after(node('div','cc-set-contents',contents));
          }
          if (photoHoverCards) alternatePhoto(card,stage,toolbar);
        }
      });
      const cards = [...productGrid.querySelectorAll('.new-card')];
      if (editorialTemplates) {
        syncEditorialCards(cards);
      } else {
      const storyPosition=surfaceCards?6:8, lifestylePosition=surfaceCards?12:16;
      if (cards.length >= storyPosition && !productGrid.querySelector('.cc-story') && unfiltered() && grid.currentSortKey === 'COLLECTION_DEFAULT') cards[storyPosition-1].after(root.querySelector('[data-cc-story]').content.cloneNode(true));
      if (cards.length >= lifestylePosition && !productGrid.querySelector('.cc-lifestyle') && unfiltered() && grid.currentSortKey === 'COLLECTION_DEFAULT') cards[lifestylePosition-1].after(root.querySelector('[data-cc-lifestyle]').content.cloneNode(true));
      }
      if (grid.productsFetched && !cards.length && !grid.isFetching && !productGrid.querySelector('.cc-empty')) {
        const empty = node('div','cc-empty'); empty.append(node('p','',strings.ui.empty)); const reset=node('button','cc-primary',strings.ui.reset);reset.type='button'; reset.addEventListener('click',()=>root.querySelector('[data-cc-filter="all"]').click());empty.append(reset); productGrid.append(empty);
      }
      sync();
    }
    const observer = new MutationObserver(enhance); observer.observe(productGrid, { childList: true }); enhance();
    if (editorialMobile) on(editorialMobile, 'change', enhance);
    root.querySelectorAll('[data-cc-pick]').forEach(pick => {
      const source = media[pick.dataset.ccPick], image = pick.querySelector('img');
      if (source && image) { image.src = source + '&width=500'; image.removeAttribute('srcset'); image.width=500;image.height=500; }
    });
    on(root.querySelector('.cc-size-options'), 'click', event => {
      const button = event.target.closest('[data-cc-size]'); if (!button) return;
      chosenSize = button.dataset.ccSize;
      root.querySelectorAll('[data-cc-size]').forEach(option => option.setAttribute('aria-pressed', String(option === button)));
      root.querySelector('[data-cc-size-visual]').dataset.ccSizeVisual = chosenSize;
      const explainer = root.querySelector('[data-cc-size-explainer="'+chosenSize+'"]');
      root.querySelector('[data-cc-size-copy]').replaceChildren(...(explainer ? [explainer.content.cloneNode(true)] : [node('h3','',strings.sizes[chosenSize+'_title']),node('p','',strings.sizes[chosenSize+'_copy'])]));
      root.querySelector('[data-cc-size-status]').textContent = '';
    });
    on(root.querySelector('[data-cc-browse-size]'), 'click', async () => {
      const button = root.querySelector('[data-cc-browse-size]'); button.disabled = true;
      try {
        if (!(await loaded())) { root.querySelector('[data-cc-size-status]').textContent = strings.ui.error; return; }
        const lengthEntry = Object.entries(grid.currentFilterTypes).find(([title]) => /(?:blade|knife).*length/i.test(title));
        const options = lengthEntry?.[1] || [];
        const matches = options.filter(value => { const number = parseFloat(value); return Number.isFinite(number) && (chosenSize === 'compact' ? number >= 6 && number < 8 : chosenSize === 'everyday' ? number >= 8 && number < 9 : number >= 9); });
        if (!matches.length) { root.querySelector('[data-cc-size-status]').textContent = strings.ui.size_no_match; return; }
        grid.selectedOptions = grid.selectedOptions.filter(value => !options.includes(value)).concat(matches);
        grid.filterAndRenderProducts(); dialogFor('size').close(); grid.scrollIntoView({ behavior: 'auto', block: 'start' });
      } finally { button.disabled = false; }
    });
    const drawer = grid.querySelector('.option-picker-list-container'), picker = grid.querySelector('.filter-picker'), filterButton=grid.querySelector('.option-picker-btn');
    drawer.setAttribute('role','dialog'); drawer.setAttribute('aria-label',strings.ui.filters); drawer.setAttribute('aria-modal','true');
    let drawerWasOpen = false;
    const drawerObserver = new MutationObserver(() => {
      const active = picker.classList.contains('active'); drawer.inert = !active; drawer.setAttribute('aria-hidden', String(!active)); filterButton.setAttribute('aria-expanded',String(active));
      if (active && !drawerWasOpen) drawer.querySelector('.option-picker-close').focus({preventScroll:true});
      if (!active && drawerWasOpen) { filterButton.focus({preventScroll:true}); sync(); }
      drawerWasOpen = active;
    }); drawerObserver.observe(picker,{attributes:true,attributeFilter:['class']});drawer.inert=true;drawer.setAttribute('aria-hidden','true');filterButton.setAttribute('aria-expanded','false');
    function accessibleFilters() {
      drawer.querySelectorAll('.sub-item').forEach(item => {
        if (/^In Stock\s*(?:\(\d+\))?$/.test(item.textContent.trim().replace(/\s+/g, ' '))) item.hidden = true;
      });
      drawer.querySelectorAll('.option-picker-list-item-title,.sub-item,.sort-picker-item').forEach(item => { item.tabIndex=0;item.setAttribute('role','button');if(item.classList.contains('sub-item'))item.setAttribute('aria-pressed',String(!!item.querySelector('.custom-checkbox.checked'))); });
    }
    const filterObserver=new MutationObserver(accessibleFilters);filterObserver.observe(drawer,{childList:true,subtree:true});accessibleFilters();
    on(drawer,'keydown',event=>{
      if(event.key==='Escape'){event.preventDefault();grid.closeDrawer();return;}
      if((event.key==='Enter'||event.key===' ')&&event.target.matches('.option-picker-list-item-title,.sub-item,.sort-picker-item')){event.preventDefault();event.target.click();return;}
      if(event.key==='Tab') {const focus=[...drawer.querySelectorAll('button,input,select,[tabindex="0"]')].filter(item=>item.getClientRects().length&&!item.disabled);const first=focus[0],last=focus[focus.length-1];if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus();}else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}}
    });
    lifecycle.signal.addEventListener('abort',()=>{observer.disconnect();drawerObserver.disconnect();filterObserver.disconnect();grid.prepareCustomProductCardsData=originalPrepare;});
    if (customCards) {
      installCollectionData();
      customElements.get('collection-grid-new').prototype.connectedCallback.call(grid);
      grid.fetchCollectionProducts(false);
    }

    function installCollectionData() {
      const records = new Map();
      const requests = new Map();
      const initialIds = [...productGrid.querySelectorAll('[data-card-product-id]')].map(card => card.dataset.cardProductId);
      const initialNodes = new Map([...productGrid.querySelectorAll('[data-card-product-id]')].map(card => [card.dataset.cardProductId, card]));
      let activeDataKey, suppressRender = false, morePending = false, revision = 0;
      const nativeFilter = grid.filterAndRenderProducts.bind(grid);
      const sortKey = () => grid.currentSortKey === 'BEST_SELLING' ? 'best-selling' : grid.currentSortKey === 'CREATED' ? 'created-descending' : 'default';
      const countText = (start, end, total) => strings.ui.pagination_range.replace('CC_START', start).replace('CC_END', end).replace('CC_TOTAL', total);

      async function fetchData(key) {
        if (!requests.has(key)) {
          const request = (async () => {
            const products = [], ids = new Set();
            let page = 1, filterTypes, total = 0;
            while (page) {
              const url = new URL(grid.dataset.cardDataUrl, location.origin);
              if (key !== 'default') url.searchParams.set('sort_by', key);
              url.searchParams.set('page', page);
              const themeId = new URL(location.href).searchParams.get('preview_theme_id') || window.Shopify?.theme?.id;
              if (themeId) url.searchParams.set('preview_theme_id', themeId);
              const response = await fetch(url, {signal: lifecycle.signal, credentials: 'same-origin'});
              if (!response.ok) throw new Error('Collection data request failed: ' + response.status);
              const data = await response.json();
              if (!Array.isArray(data.products)) throw new Error('Invalid collection data response');
              filterTypes = data.filterTypes; total = data.total;
              data.products.forEach(record => {
                const id = String(record.node.id);
                if (ids.has(id)) throw new Error('Duplicate product in collection data');
                ids.add(id); products.push({node:record.node}); records.set(id, record);
                details[record.node.handle] = record.details;
                grid.reviewsRatings[record.node.handle] = record.rating;
              });
              const next = data.nextPage;
              if (next && next <= page) throw new Error('Invalid collection page sequence');
              page = next;
            }
            if (products.length !== total) throw new Error('Incomplete collection data');
            return {products, filterTypes};
          })().catch(error => { requests.delete(key); throw error; });
          requests.set(key, request);
        }
        return requests.get(key);
      }

      grid.fetchCollectionProducts = async function (shouldRender = true) {
        const key = sortKey();
        this.isFetching = true;
        if (shouldRender) status.textContent = strings.ui.loading;
        try {
          const data = await fetchData(key);
          if (lifecycle.signal.aborted || key !== sortKey()) return false;
          if (activeDataKey !== key || !this.productsFetched) {
            this.allProducts = data.products;
            const filterTypes = data.filterTypes?.references?.nodes?.length ? data.filterTypes : {
              references: {nodes: JSON.parse(this.defaultFilterTypes || '[]').map(type => ({fields: [
                {key: 'title', value: type.title},
                {key: 'options', value: JSON.stringify(type.options)}
              ]}))}
            };
            this.configureFilterTypes(this.allProducts, filterTypes);
            this.productsFetched = true;
            activeDataKey = key;
            // Compute the full filtered list without replacing the server-rendered cards.
            suppressRender = true;
            try { nativeFilter(); } finally { suppressRender = false; }
            if (unfiltered() && this.currentSortKey === 'COLLECTION_DEFAULT') {
              const byId = new Map(this.filteredProducts.map(item => [String(item.node.id), item]));
              const initialSet = new Set(initialIds);
              this.filteredProducts = initialIds.map(id => byId.get(id)).filter(Boolean).concat(this.filteredProducts.filter(item => !initialSet.has(String(item.node.id))));
            }
          }
          status.textContent = '';
          if (shouldRender) this.filterAndRenderProducts();
          return true;
        } catch (error) {
          if (error.name !== 'AbortError') { status.textContent = strings.ui.error; console.error('Chef collection data:', error); }
          return false;
        } finally {
          if (key === sortKey()) {
            this.isFetching = false;
            this.renderFilterTypes();
            sync();
          }
        }
      };

      grid.renderProducts = function (products, append = false) {
        if (suppressRender) return;
        const existing = new Set(append ? [...productGrid.querySelectorAll('[data-card-product-id]')].map(card => card.dataset.cardProductId) : []);
        const fragment = document.createDocumentFragment();
        products.forEach(({node:product}) => {
          const id = String(product.id);
          if (existing.has(id)) return;
          existing.add(id);
          const record = records.get(id);
          if (!record) throw new Error('Missing Liquid card for product ' + id);
          // Reuse the initial elements, including native widget state, when filters are reset.
          const original = initialNodes.get(id);
          if (original) fragment.append(original);
          else {
            const template = document.createElement('template');
            template.innerHTML = record.card;
            fragment.append(template.content);
          }
        });
        if (!append) productGrid.replaceChildren();
        productGrid.append(fragment);
        this.querySelector('.collection-counter').textContent = '(' + this.filteredProducts.length + ' ' + strings.ui.results + ')';
        this.reinitYotpoWidgets();
      };

      grid.renderPaginationControls = function (total) {
        if (suppressRender) return;
        let pagination = this.querySelector('.collection-pagination');
        if (!pagination) { pagination = node('div','collection-pagination'); this.append(pagination); }
        const count = productGrid.querySelectorAll('[data-card-product-id]').length;
        pagination.hidden = !total;
        pagination.replaceChildren(node('div','collection-pagination-range',countText(total ? 1 : 0, count, total)));
        if (count < total) {
          const button = node('button','collection-pagination-load-more',strings.ui.load_more);
          button.type = 'button'; pagination.append(button);
        }
      };
      grid.filterAndRenderProducts = function () {
        revision++;
        if (!this.productsFetched) { this.fetchCollectionProducts(true); return; }
        nativeFilter();
        sync();
      };
      grid.applyUrlFilters = function () {
        const filter = new URL(location.href).searchParams.get('filter');
        if (!filter) return;
        this.selectedOptions = filter.split(',').map(value => /^\d+$/.test(value.trim()) ? value.trim() + '%' : value.trim()).filter(Boolean);
        this.hasUrlFilters = this.selectedOptions.length > 0;
        if (this.hasUrlFilters) this.fetchCollectionProducts(true);
      };
      grid.addEventListener('click', async event => {
        const button = event.target.closest('.collection-pagination-load-more');
        if (!button) return;
        event.preventDefault(); event.stopImmediatePropagation();
        if (morePending) return;
        morePending = true;
        const requestedRevision = revision;
        button.disabled = true; button.setAttribute('aria-busy','true'); button.textContent = strings.ui.loading;
        try {
          if (!(await loaded()) || requestedRevision !== revision) return;
          const visible = new Set([...productGrid.querySelectorAll('[data-card-product-id]')].map(card => card.dataset.cardProductId));
          const next = grid.filteredProducts.filter(({node:product}) => !visible.has(String(product.id))).slice(0, grid.pageSize);
          grid.renderProducts(next, true);
          grid.currentPage = Math.ceil(productGrid.querySelectorAll('[data-card-product-id]').length / grid.pageSize);
          grid.renderPaginationControls(grid.filteredProducts.length);
        } finally {
          morePending = false;
          if (button.isConnected) { button.disabled = false; button.removeAttribute('aria-busy'); button.textContent = strings.ui.load_more; }
        }
      }, {capture:true, signal:lifecycle.signal});
    }
  }
  const start = scope => scope.querySelectorAll('[data-chef-collection]').forEach(init);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>start(document),{once:true});else start(document);
  document.addEventListener('shopify:section:load',event=>start(event.target));
  document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('[data-chef-collection]').forEach(root=>instances.get(root)?.abort()));
})();
